import { useEffect } from 'react'
import { useStyletron } from 'styletron-react'
import { useDispatch, useSelector } from 'react-redux'
import { lightTheme } from '@/customTheme'
import { getModelThumbnailUrl, getModelHighresUrl } from '@/utils/getResizeUrl'
import { getAIFashionModels } from '@/store/slices/aiFashionModels/actions'
import { selectAIFashionModels } from '@/store/slices/aiFashionModels/selectors'
import defaultImage from '@assets/images/default-image.png'
import type { AppDispatch } from '@/store/store'

const DEFAULT_ASPECT_RATIO = 194.4 / 260
const DEFAULT_GHOST_COUNT = 12

export type FashionModel = {
  id: string
  name: string
  /** High-res asset for editor/AI-studio handoff. */
  imageUrl: string | null
  /** Resized asset for grid display. */
  thumbnailUrl: string | null
  src?: string | null
  prompt?: string
  gender?: string
  /** Age category from the template (`age_category`), e.g. 'baby', 'toddler'. */
  age?: string
  classificationId?: string
}

/**
 * Single source of truth for the AI fashion-model list: fetches once if needed
 * and maps the raw slice data into ready-to-render {@link FashionModel}s
 * (thumbnail + high-res URLs resolved). Shared by every model grid.
 */
export function useFashionModels(): {
  models: FashionModel[]
  isLoading: boolean
  aspectRatio: number | string
} {
  const dispatch = useDispatch<AppDispatch>()
  const fashionModels = useSelector(selectAIFashionModels)

  useEffect(() => {
    if (!fashionModels) dispatch(getAIFashionModels())
  }, [dispatch, fashionModels])

  const models: FashionModel[] =
    fashionModels?.data.map((model, index) => ({
      id: model.id ?? String(index),
      name: model.title,
      imageUrl: model.src ? getModelHighresUrl(model.src) : model.src ?? null,
      thumbnailUrl: model.src ? getModelThumbnailUrl(model.src) : model.src ?? null,
      src: model.src,
      prompt: model.prompt,
      gender: model.gender,
      age: model.age,
      classificationId: model.classificationId,
    })) ?? []

  return {
    models,
    isLoading: !fashionModels,
    aspectRatio: fashionModels?.aspectRatio ?? DEFAULT_ASPECT_RATIO,
  }
}

type Props = {
  onSelect: (model: FashionModel) => void
  /** Number of columns; defaults to 3. */
  columns?: number
  selectedId?: string | null
  disabled?: boolean
  ghostCount?: number
}

/** Grid of selectable fashion-model cards with loading ghosts. The caller owns
 * the scroll container (e.g. wrap it in <AutoScroll>). */
function FashionModelGrid({ onSelect, columns = 3, selectedId, disabled, ghostCount = DEFAULT_GHOST_COUNT }: Props) {
  const { models, isLoading, aspectRatio } = useFashionModels()
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '8px',
        width: '100%',
      }}
    >
      {isLoading
        ? Array.from({ length: ghostCount }).map((_, i) => <GhostCell key={i} aspectRatio={aspectRatio} />)
        : models.map(model => (
            <ModelCell
              key={model.src ?? model.id}
              thumbnailUrl={model.thumbnailUrl}
              aspectRatio={aspectRatio}
              isSelected={selectedId === model.id}
              isDisabled={!!disabled}
              onClick={() => onSelect(model)}
            />
          ))}
    </div>
  )
}

function GhostCell({ aspectRatio }: { aspectRatio: number | string }) {
  return (
    <div
      style={{
        aspectRatio: String(aspectRatio),
        borderRadius: '8px',
        backgroundColor: lightTheme.colors.grayScale50,
        border: '1px solid rgba(0,0,0,0.1)',
      }}
    />
  )
}

function ModelCell({
  thumbnailUrl,
  aspectRatio,
  isSelected,
  isDisabled,
  onClick,
}: {
  thumbnailUrl: string | null
  aspectRatio: number | string
  isSelected: boolean
  isDisabled: boolean
  onClick: () => void
}) {
  const [css] = useStyletron()
  const src = thumbnailUrl ?? defaultImage
  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={e => {
        if (isDisabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={css({
        position: 'relative',
        aspectRatio: String(aspectRatio),
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: isDisabled ? 'default' : 'pointer',
        boxShadow: isSelected
          ? `inset 0 0 0 2px ${lightTheme.colors.primary}`
          : 'inset 0 0 0 1px rgba(0,0,0,0.1)',
        backgroundColor: lightTheme.colors.white,
        transition: 'box-shadow 0.15s ease',
        ':hover': isDisabled ? undefined : { boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.3)' },
      })}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  )
}

export default FashionModelGrid
